'use client';

import { useState } from 'react';
import { RichContent } from '../../RichContent/RichContent';
import { DataTable } from '../../TableComponent/TableComponent';
import { Select } from '../../form-controls/Select/Select';
import type { StaticTableBlock, StaticTableColumn } from '../types';

type StaticTableProps = {
  block: StaticTableBlock;
  'data-testid'?: string;
};

const CELL_CLASSNAME = 'inline text-base leading-6 font-light';

/** Sanitise each string cell through {@link RichContent} for {@link DataTable}. */
function toDataTableRows(rows: string[][]) {
  return rows.map((cells, rowIndex) => ({
    cells: cells.map((cell, cellIndex) => <RichContent key={`${rowIndex}-${cellIndex}`} tag="span" information={cell} className={CELL_CLASSNAME} />),
  }));
}

/**
 * Table sourced from static page JSON.
 *
 * The default `grid` variant maps `columns`/`rows` straight onto {@link DataTable}.
 * The `picker` variant is a client-side dropdown over the block's groups: the
 * selected group is prepared into a two-column {@link DataTable} (row label +
 * value), so a wide, sparse matrix reads as one short table per option. Cell
 * strings are sanitised through {@link RichContent}; keep visible titles as
 * preceding prose in the JSON rather than relying on the table caption.
 *
 * @param block - A grid or picker table block from page JSON
 */
function StaticTable({ block, 'data-testid': dataTestId = 'static-table' }: StaticTableProps) {
  const [selectedOption, setSelectedOption] = useState(() => (block.variant === 'picker' ? (block.groups[0]?.option ?? '') : ''));

  if (block.variant === 'picker') {
    const group = block.groups.find(candidate => candidate.option === selectedOption);
    const columns: StaticTableColumn[] = [{ header: block.rowHeader ?? 'Match' }, { header: block.valueHeader ?? 'Prize', align: 'right', mobileAlign: 'left' }];
    const rows = toDataTableRows((group?.rows ?? []).map(row => [row.label, row.value]));

    return (
      <div className="my-4 flex w-full flex-col gap-4 p-5 bg-container rounded-lg" data-testid={dataTestId}>
        <Select
          id={`${dataTestId}-select`}
          label={block.selectLabel}
          options={block.groups.map(candidate => ({ value: candidate.option, text: candidate.option }))}
          value={selectedOption}
          onChange={setSelectedOption}
          className="w-full sm:max-w-xs"
          data-testid={`${dataTestId}-select`}
        />
        <DataTable columns={columns} rows={rows} caption={block.caption} aria-label={block.ariaLabel} data-testid={`${dataTestId}-table`} />
      </div>
    );
  }

  const gridRows = block.rows.map(row => block.columns.map(column => (column.key !== undefined ? (row[column.key] ?? '') : '')));

  return <DataTable className="my-4" data-testid={dataTestId} columns={block.columns} rows={toDataTableRows(gridRows)} caption={block.caption} aria-label={block.ariaLabel} />;
}

export { StaticTable, type StaticTableProps };
